/**
 * The invite-state badge cell, shared verbatim between the global roster
 * and the client page's roster preview (PROV-ALERTS) — one cell, so the
 * two tables cannot disagree about what `failed` looks like.
 */
import { Badge, Icon, type IconName } from '@speaking-coach/ui'
import type { InviteState } from './types'

export interface InviteStateCellValue {
  inviteState: InviteState
  inviteSendFailedReason: string | null
  inviteSentAt: string | null
  inviteExpiresAt: string | null
  inviteScheduledFor?: string | null
  inviteSuppressedAt?: string | null
}

/**
 * One word per invite state, and the two that need attention marked.
 *
 * Terse rather than the detail page's full sentences — a 25-row table cannot
 * carry explanations — but the words are chosen so nothing reads as fine when
 * it is not: `failed` says nothing was delivered, `overdue` says a planned
 * send did not happen. Those are the two states an operator would never
 * think to go looking for, and the reason this column exists at all.
 */
const INVITE_STATE_SHORT: Record<InviteState, string> = {
  none: '—',
  scheduled: 'scheduled',
  overdue: 'overdue',
  suppressed: 'canceled',
  pending: 'pending',
  failed: 'failed',
  expired: 'expired',
  activated: 'activated',
}

/**
 * A small glyph beside each state's word — the mail family, so the column
 * reads as one lifecycle rather than eight unrelated chips. Decorative
 * (`aria-hidden`): the WORDS are the meaning, chosen deliberately above.
 *
 * The two operator-canceled-vs-lapsed states get different glyphs on
 * purpose: `canceled` is `inviteSuppressed` (someone stopped it), `expired`
 * is `inviteExpired` (it lapsed on its own). `none` renders no badge, so no
 * icon.
 */
const INVITE_STATE_ICON: Record<
  Exclude<InviteState, 'none'>,
  IconName
> = {
  scheduled: 'schedule',
  overdue: 'overdue',
  suppressed: 'inviteSuppressed',
  pending: 'invitePending',
  failed: 'inviteFailed',
  expired: 'inviteExpired',
  activated: 'inviteAccepted',
}

/**
 * Which badge variant each state earns.
 *
 * `attention` is reserved for the two states where doing nothing is the wrong
 * response, so the color keeps meaning something: if `expired` and `pending`
 * were red too, a scan would find red everywhere and stop conveying urgency.
 * `none` gets no badge at all — an em-dash, because "nothing planned" is not a
 * status worth a chip on every unprovisioned row.
 */
const INVITE_STATE_VARIANT: Record<
  InviteState,
  'neutral' | 'danger' | 'muted'
> = {
  none: 'muted',
  scheduled: 'muted',
  overdue: 'danger',
  suppressed: 'muted',
  pending: 'neutral',
  failed: 'danger',
  expired: 'neutral',
  activated: 'muted',
}

/**
 * The failure-reason vocabulary, worded for the operator's NEXT MOVE —
 * "check the address" vs "just press Resend" — because that is the whole
 * point of persisting a reason (PROV-ALERTS). Keys are the send seam's
 * stable codes; an unknown code falls back to itself so a vocabulary that
 * grows ahead of this map surfaces rather than vanishes.
 */
export const FAILURE_REASON_PROSE: Record<string, string> = {
  'rejected_hard-bounce':
    'The address hard-bounced — it does not exist or refuses mail. Check it for typos before resending.',
  'rejected_soft-bounce':
    'Their mailbox turned it away temporarily — often a full inbox. Resend usually works.',
  rejected_spam: 'Their mail system flagged it as spam.',
  rejected_unsub: 'The address has unsubscribed from our mail.',
  rejected_unknown: 'The email service refused the address without saying why.',
  invalid_recipient: 'The address is not a valid email address. Correct it, then resend.',
  timeout: 'The email service timed out. Resend usually works.',
  provider_5xx: 'The email service had an outage. Resend once it recovers.',
  network: 'The email service could not be reached. Resend usually works.',
  rate_limited: 'The email service rate-limited us. Wait a little, then resend.',
  malformed_response: 'The email service answered unusably. Resend usually works.',
  http_4xx: 'The email service refused the request.',
  quota_exceeded: 'Our email sending quota is exhausted.',
  template_misconfigured:
    'The email template is not configured. Resending cannot fix this — it needs an engineer.',
  provider_unconfigured:
    'Email sending is not configured in this environment. It needs an engineer.',
  window_closed: 'Their program access ended before the send fired, so no email was attempted.',
  'suppressed_hard-bounce':
    'This address is marked undeliverable (it hard-bounced before), so no email was attempted. Fix the address to resume mail.',
  suppressed_spam:
    'This address is marked undeliverable (a previous email was reported as spam), so no email was attempted.',
  suppressed_unsub:
    'This address is marked undeliverable (it unsubscribed at the mail-provider level), so no email was attempted.',
  'suppressed_soft-bounce-repeated':
    'This address is marked undeliverable (it kept bouncing over two weeks), so no email was attempted. Fix the address to resume mail.',
  suppressed_manual:
    'This address was manually marked undeliverable, so no email was attempted.',
  unknown: 'The send failed for an unrecognized reason. Resend, and escalate if it repeats.',
}

/** Operator prose for a failure reason code — never empty, never lost. */
export function failureReasonProse(reason: string): string {
  return FAILURE_REASON_PROSE[reason] ?? `Reason: ${reason}.`
}

/** ISO date/timestamp → YYYY-MM-DD. Locale-independent on purpose. */
function isoDate(value: string | null): string {
  return value ? value.slice(0, 10) : '—'
}

export function InviteStateCell({
  student,
}: {
  student: InviteStateCellValue
}): JSX.Element {
  const scheduledFor =
    student.inviteState === 'scheduled' || student.inviteState === 'overdue'
      ? (student.inviteScheduledFor ?? null)
      : null
  // The state's one relevant date, below the badge. Each state gets the
  // date that answers its own "when": due / due-and-missed / mailed /
  // canceled / lapsed. Null when the row predates the stamp.
  const dateLine = (() => {
    switch (student.inviteState) {
      case 'scheduled':
        return scheduledFor !== null ? isoDate(scheduledFor) : null
      case 'overdue':
        return scheduledFor !== null ? `since ${isoDate(scheduledFor)}` : null
      case 'pending':
        return student.inviteSentAt !== null ? `sent on ${isoDate(student.inviteSentAt)}` : null
      case 'suppressed':
        return student.inviteSuppressedAt != null
          ? `on ${isoDate(student.inviteSuppressedAt)}`
          : null
      case 'expired':
        return student.inviteExpiresAt !== null
          ? `on ${isoDate(student.inviteExpiresAt)}`
          : null
      default:
        return null
    }
  })()
  if (student.inviteState === 'none') {
    return <span className="text-text-secondary">—</span>
  }
  // The failed badge carries its reason as a tooltip, so a roster scan can
  // answer "typo or outage?" without opening each student.
  const failedReason =
    student.inviteState === 'failed' && student.inviteSendFailedReason !== null
      ? failureReasonProse(student.inviteSendFailedReason)
      : null
  return (
    <span
      title={
        failedReason ??
        (scheduledFor !== null
          ? `Send ${student.inviteState === 'overdue' ? 'was due' : 'due'} ${isoDate(scheduledFor)}`
          : undefined)
      }
    >
      <Badge variant={INVITE_STATE_VARIANT[student.inviteState]}>
        <Icon name={INVITE_STATE_ICON[student.inviteState]} size={14} />
        {INVITE_STATE_SHORT[student.inviteState]}
      </Badge>
      {/* Second line below the badge, same shape as the program-window
          cell's length line, so the two-line cells read as one pattern. */}
      {dateLine !== null && (
        <span className="block text-caption tabular-nums text-text-secondary mt-0.5">
          {dateLine}
        </span>
      )}
    </span>
  )
}
